import { useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { MyContext } from '../../../context';
import SimpleDropdown from '../../../components/SimpleDropdown';
import Switch from '../../../components/Switch';
import { createProduct, updateProduct, listCategories } from '../../../utils/queries';

const emptyForm = {
	productNumber: '',
	name: '',
	displayName: '',
	shortDescription: '',
	description: '',
	status: 'active',
	featuredFlag: false,
	exemptShippingCharges: false,
	exemptTaxes: false,
	continueSellingOutOfStock: false,
	safetyStockQty: '',
	categoryId: '',
	sortOrder: 0,
};

export default function DetailsTab({ product, storeId, kitMode = false, onSaved }) {
	const { activeStoreId: globalActiveStoreId } = useContext(MyContext);
	const [values, setValues] = useState(
		product
			? {
					productNumber: product.productNumber || '',
					name: product.name || '',
					displayName: product.displayName || '',
					shortDescription: product.shortDescription || '',
					description: product.description || '',
					status: product.status || 'active',
					featuredFlag: !!product.featuredFlag,
					exemptShippingCharges: !!product.exemptShippingCharges,
					exemptTaxes: !!product.exemptTaxes,
					continueSellingOutOfStock: !!product.continueSellingOutOfStock,
					safetyStockQty: product.safetyStockQty ?? '',
					categoryId: product.categoryId ?? '',
					sortOrder: product.sortOrder ?? 0,
				}
			: emptyForm
	);
	const [categories, setCategories] = useState([]);
	const [saving, setSaving] = useState(false);

	const activeStoreId = product ? product.storeId : storeId || globalActiveStoreId;

	useEffect(() => {
		if (!activeStoreId) return;
		listCategories({ storeId: activeStoreId, limit: 100 }).then((result) => {
			if (result?.success) setCategories(result.data.categories);
		});
	}, [activeStoreId]);

	const handleSubmit = async (e) => {
		e.preventDefault();
		setSaving(true);

		const payload = {
			...values,
			categoryId: values.categoryId === '' ? null : Number(values.categoryId),
			sortOrder: Number(values.sortOrder),
			safetyStockQty: values.safetyStockQty === '' ? null : Number(values.safetyStockQty),
		};
		const result = product
			? await updateProduct(product.productId, payload)
			: await createProduct({ ...payload, storeId: activeStoreId, productType: kitMode ? 'kit' : 'simple' });

		setSaving(false);
		if (!result?.success) {
			toast.error(result?.error?.message || 'Failed to save.');
			return;
		}
		toast.success('Saved.');
		onSaved?.(result.data);
	};

	return (
		<form onSubmit={handleSubmit} className='space-y-4 max-w-lg'>
			<div className="w-1/3">
				<label>Product Number</label>
				<input
					className='form-input max'
					required
					value={values.productNumber}
					onChange={(e) => setValues({ ...values, productNumber: e.target.value })}
				/>
			</div>
			<div>
				<label>Name</label>
				<input
					className='form-input'
					required
					value={values.name}
					onChange={(e) => setValues({ ...values, name: e.target.value })}
				/>
			</div>
			<div>
				<label>Display Name</label>
				<input
					className='form-input'
					value={values.displayName}
					onChange={(e) => setValues({ ...values, displayName: e.target.value })}
				/>
			</div>
			<div>
				<label>Short Description</label>
				<textarea
					className='form-input h-16'
					value={values.shortDescription}
					onChange={(e) => setValues({ ...values, shortDescription: e.target.value })}
				/>
			</div>
			<div>
				<label>Description</label>
				<textarea
					className='form-input h-24'
					value={values.description}
					onChange={(e) => setValues({ ...values, description: e.target.value })}
				/>
			</div>
			<div>
				<label>Category</label>
				<SimpleDropdown
					value={values.categoryId}
					onChange={(v) => setValues({ ...values, categoryId: v })}
					options={[
						{ value: '', label: 'None' },
						...categories.map((c) => ({ value: c.categoryId, label: c.name })),
					]}
					className="w-56"
				/>
			</div>
			<div className='pt-3 divide-y divide-gray-100 border-y border-gray-100'>
				<div className='flex items-center justify-between gap-4 py-4'>
					<p className='text-sm font-medium text-gray-900'>Featured</p>
					<Switch
						checked={values.featuredFlag}
						onChange={(checked) => setValues({ ...values, featuredFlag: checked })}
					/>
				</div>
				<div className='flex items-center justify-between gap-4 py-4'>
					<p className='text-sm font-medium text-gray-900'>Exempt Shipping Charges</p>
					<Switch
						checked={values.exemptShippingCharges}
						onChange={(checked) => setValues({ ...values, exemptShippingCharges: checked })}
					/>
				</div>
				<div className='flex items-center justify-between gap-4 py-4'>
					<p className='text-sm font-medium text-gray-900'>Exempt Taxes</p>
					<Switch
						checked={values.exemptTaxes}
						onChange={(checked) => setValues({ ...values, exemptTaxes: checked })}
					/>
				</div>
				<div className='flex items-center justify-between gap-4 py-4'>
					<div>
						<p className='text-sm font-medium text-gray-900'>Continue selling when out of stock</p>
						<p className='text-sm text-gray-400'>
							When on, customers can still order this product after inventory reaches zero.
						</p>
					</div>
					<Switch
						checked={values.continueSellingOutOfStock}
						onChange={(checked) => setValues({ ...values, continueSellingOutOfStock: checked })}
					/>
				</div>
				<div className='flex items-center justify-between gap-4 py-4'>
					<div>
						<p className='text-sm font-medium text-gray-900'>Low-Inventory Quantity</p>
						<p className='text-sm text-gray-400'>
							Flag this product as a stock risk when available quantity drops
							below this number.
						</p>
					</div>
					<input
						type='number'
						min='0'
						className='form-input w-16'
						value={values.safetyStockQty}
						onChange={(e) => setValues({ ...values, safetyStockQty: e.target.value })}
					/>
				</div>
				<div className='flex items-center justify-between gap-4 py-4'>
					<p className='text-sm font-medium text-gray-900'>Sort Order</p>
					<input
						type='number'
						className='form-input w-16'
						value={values.sortOrder}
						onChange={(e) => setValues({ ...values, sortOrder: e.target.value })}
					/>
				</div>				
				<div className='flex items-center justify-between gap-4 py-4'>
					<p className='text-sm font-medium text-gray-900'>Status</p>
					<SimpleDropdown
						value={values.status}
						onChange={(v) => setValues({ ...values, status: v })}
						options={[
							{ value: 'active', label: 'Active' },
							{ value: 'inactive', label: 'Inactive' },
							// 'discontinued' is set automatically when ERP's sync stops
							// seeing the item (see syncWorkers.runProductSync) -- only
							// offered here so an already-discontinued product's true
							// status still displays correctly.
							...(values.status === 'discontinued' ? [{ value: 'discontinued', label: 'Discontinued' }] : []),
						]}
						className="w-36"
					/>
				</div>
			</div>
			<button type='submit' disabled={saving} className='btn-primary'>
				{saving ? 'Saving...' : product ? 'Save' : kitMode ? 'Create Kit' : 'Create Product'}
			</button>
			{!product && (
				<p className='text-xs text-gray-400'>
					{kitMode
						? 'Save to unlock Kit Items, Pricing, and Images.'
						: 'Save to unlock Options & Variants, Pricing, and Images.'}
				</p>
			)}
		</form>
	);
}
